import { apiGet, apiPost } from './client';
import type {
  CursorPage,
  WalletDto,
  WalletLedgerRow,
  InternalWalletSummary,
  InternalWalletLedgerRow,
  InternalWalletLedgerFilter,
  WalletLedgerFilterOptions,
} from './types';

export const walletApi = {
  get: (merchantId: string) => apiGet<WalletDto>(`/api/merchants/${merchantId}/wallet`),
  ledger: (merchantId: string, params: { cursor?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return apiGet<CursorPage<WalletLedgerRow>>(
      `/api/merchants/${merchantId}/wallet/ledger${qs ? `?${qs}` : ''}`,
    );
  },
  /** Internal-only: platform-wide wallet totals across all merchants. */
  internalSummary: () => apiGet<InternalWalletSummary>('/api/wallet/internal/summary'),
  /** Internal-only: cross-merchant balance-mutation feed (filtered + cursor paginated). */
  internalLedger: (params: InternalWalletLedgerFilter = {}) => {
    const q = new URLSearchParams();
    if (params.trxId) q.set('trxId', params.trxId);
    params.merchantId?.forEach((m) => q.append('merchantId', m));
    params.type?.forEach((t) => q.append('type', t));
    params.createdBy?.forEach((c) => q.append('createdBy', c));
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return apiGet<CursorPage<InternalWalletLedgerRow>>(
      `/api/wallet/internal/ledger${qs ? `?${qs}` : ''}`,
    );
  },
  /** Internal-only: option lists (merchants + createdBy) for the ledger filters. */
  internalLedgerFilters: () =>
    apiGet<WalletLedgerFilterOptions>('/api/wallet/internal/ledger/filters'),
  adjust: (
    merchantId: string,
    input: { amountPending?: number; amountActive?: number; amountReserved?: number; reason: string },
  ) => apiPost<{ ok: true }>(`/api/merchants/${merchantId}/wallet/adjustments`, input),
  /**
   * Add or deduct active balance (internal-only). A deduct exceeding active
   * requires carryover=true; the shortfall is paid down from future settlements.
   */
  adjustActive: (
    merchantId: string,
    input: { direction: 'add' | 'deduct'; amount: number; carryover?: boolean; reason: string },
  ) =>
    apiPost<{
      ok: true;
      appliedToActive: number;
      carriedOver: number;
      newActiveBalance: number;
      newOutstandingBalance: number;
    }>(`/api/merchants/${merchantId}/wallet/adjust-active`, input),
};
